/**
 * OSRM Route service — road geometry for drawing (public demo; optional REACT_APP_OSRM_BASE_URL).
 */

const DEFAULT_BASE = 'https://router.project-osrm.org';

export function getOsrmBaseUrl(): string {
  const u = String(process.env.REACT_APP_OSRM_BASE_URL || '').trim().replace(/\/$/, '');
  return u || DEFAULT_BASE;
}

/** OSRM public demo caps; chain requests above this. */
const MAX_COORDS_PER_REQUEST = 95;

export type RoadLegSegment = {
  solid: boolean;
  positions: [number, number][];
};

function decodeGeoJsonLineString(geom: unknown): [number, number][] | null {
  if (!geom || typeof geom !== 'object') return null;
  const g = geom as { type?: string; coordinates?: unknown };
  if (g.type !== 'LineString' || !Array.isArray(g.coordinates)) return null;
  const out: [number, number][] = [];
  for (const pt of g.coordinates) {
    if (!Array.isArray(pt) || pt.length < 2) continue;
    const lng = Number(pt[0]);
    const lat = Number(pt[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    out.push([lat, lng]);
  }
  return out.length >= 2 ? out : null;
}

function concatPositions(a: [number, number][], b: [number, number][]): [number, number][] {
  if (!a.length) return b;
  if (!b.length) return a;
  const last = a[a.length - 1];
  const first = b[0];
  if (last[0] === first[0] && last[1] === first[1]) return [...a, ...b.slice(1)];
  return [...a, ...b];
}

function chordLegSegmentsForSlice(
  slice: { latitude: number; longitude: number }[],
  legSolids: boolean[]
): RoadLegSegment[] {
  const out: RoadLegSegment[] = [];
  for (let i = 0; i < slice.length - 1; i++) {
    const a = slice[i];
    const b = slice[i + 1];
    out.push({
      solid: legSolids[i] ?? false,
      positions: [
        [a.latitude, a.longitude],
        [b.latitude, b.longitude],
      ],
    });
  }
  return out;
}

type RouteLeg = {
  steps?: { geometry?: unknown }[];
  geometry?: unknown;
};

/**
 * One OSRM Route request for a coordinate slice. On failure or odd response, returns straight chords for that slice.
 */
async function fetchRouteChunkPolylines(
  base: string,
  slice: { latitude: number; longitude: number }[],
  legSolids: boolean[]
): Promise<RoadLegSegment[]> {
  if (slice.length < 2) return [];
  if (legSolids.length !== slice.length - 1) return chordLegSegmentsForSlice(slice, legSolids);

  const coordStr = slice.map((p) => `${p.longitude},${p.latitude}`).join(';');
  const url = `${base}/route/v1/driving/${coordStr}?geometries=geojson&overview=full&steps=true`;

  try {
    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'User-Agent': 'SkimBusterWeb/1.0 (sweep map route geometry; +https://skimbuster.com)',
      },
    });
    if (!res.ok) return chordLegSegmentsForSlice(slice, legSolids);

    const j = (await res.json()) as {
      code?: string;
      routes?: { geometry?: unknown; legs?: RouteLeg[] }[];
    };
    if (j.code !== 'Ok' || !Array.isArray(j.routes) || !j.routes[0]) {
      return chordLegSegmentsForSlice(slice, legSolids);
    }

    const route = j.routes[0];
    const legs = route.legs;
    const expectedLegs = slice.length - 1;

    if (Array.isArray(legs) && legs.length === expectedLegs) {
      const out: RoadLegSegment[] = [];
      for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        const steps = Array.isArray(leg.steps) ? leg.steps : [];
        let merged: [number, number][] = [];
        for (const st of steps) {
          const part = decodeGeoJsonLineString(st.geometry);
          if (part) merged = concatPositions(merged, part);
        }
        if (merged.length < 2) {
          const fromLeg = decodeGeoJsonLineString(leg.geometry);
          if (fromLeg && fromLeg.length >= 2) merged = fromLeg;
        }
        if (merged.length < 2) {
          const a = slice[i];
          const b = slice[i + 1];
          merged = [
            [a.latitude, a.longitude],
            [b.latitude, b.longitude],
          ];
        }
        out.push({
          solid: legSolids[i] ?? false,
          positions: merged,
        });
      }
      return out;
    }

    const whole = decodeGeoJsonLineString(route.geometry);
    if (whole && whole.length >= 2) {
      return [{ solid: legSolids[0] ?? false, positions: whole }];
    }
  } catch {
    /* fall through */
  }

  return chordLegSegmentsForSlice(slice, legSolids);
}

/**
 * Fetches driving route geometry between consecutive ordered waypoints.
 * `solidPerLeg[i]` applies to leg from waypoint i → i+1.
 * Chunks long routes; a failed chunk falls back to straight segments for that chunk only.
 */
export async function fetchRouteLegPolylines(
  ordered: { latitude: number; longitude: number; sweepCheckedOff?: boolean }[],
  solidPerLeg: boolean[]
): Promise<RoadLegSegment[] | null> {
  if (ordered.length < 2) return [];
  if (solidPerLeg.length !== ordered.length - 1) return null;

  const base = getOsrmBaseUrl();
  const chunks: { start: number; end: number }[] = [];
  let start = 0;
  while (start < ordered.length - 1) {
    const end = Math.min(ordered.length - 1, start + MAX_COORDS_PER_REQUEST - 1);
    chunks.push({ start, end });
    if (end >= ordered.length - 1) break;
    start = end;
  }

  const allSegments: RoadLegSegment[] = [];

  for (const { start, end } of chunks) {
    const slice = ordered.slice(start, end + 1);
    if (slice.length < 2) continue;
    const legSolids = solidPerLeg.slice(start, end);
    const chunkSegs = await fetchRouteChunkPolylines(base, slice, legSolids);
    allSegments.push(...chunkSegs);
  }

  return allSegments.length ? allSegments : null;
}
